import { groupBy } from 'lodash-es'

import { getMappedColors } from '../colorsMap'
import { getAxisLabelFormatter } from '../formatChartData'
import { ChartData } from '@/Atomic/Organisms/Charts/acBar/dataConverter'
import { isDevEnv } from '@/utils/isRuntimeEnv'
import { getCurrentLocale } from '@/utils/localeMapping'

export function getLineChartOptionsAndData(
  data: ChartData,
  showLegend: boolean,
  isExpanded = false,
) {
  const locale = getCurrentLocale()

  try {
    const { metaData, chartData = [] } = data

    if (isDevEnv()) {
      console.log(
        `>>> Raw V2 line chart data for chart "${metaData.title}":`,
        chartData,
      )
    }

    const defaultFormatter = (value: number) => value.toLocaleString(locale)
    const formatY = getAxisLabelFormatter(
      metaData.properties?.yFormat,
      locale,
      defaultFormatter,
    )

    // Handle empty chart data
    if (chartData.length === 0) {
      const emptyOptions = {
        chart: {
          type: 'line',
          width: '100%',
          height: '100%',
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: false,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: 'smooth',
          width: isExpanded ? 3 : 2,
        },
        markers: {
          size: 5,
          strokeWidth: 0,
          hover: {
            size: 7,
          },
        },
        grid: {
          padding: {
            top: 20,
            right: 20,
            bottom: 10,
            left: 10,
          },
        },
        xaxis: {
          labels: {
            show: true,
          },
          categories: [],
        },
        yaxis: {
          labels: {
            formatter: formatY,
          },
          min: 0,
        },
        colors: getMappedColors(metaData.colors),
        legend: {
          show: false,
          position: (metaData.legendPosition || 'bottom') as
            | 'top'
            | 'right'
            | 'bottom'
            | 'left',
          horizontalAlign: 'center',
        },
        title: {
          text: '',
          align: 'left',
        },
        tooltip: {
          y: {
            formatter: formatY,
          },
        },
        noData: {
          text: metaData.noDataMessage || 'No data found',
        },
      }

      return { options: emptyOptions, data: [] }
    }

    const categories = metaData.properties?.xTitles || []
    const groupTitles = metaData.properties?.groupTitles || []

    // Group chart data by group index
    const groupedData = groupBy(chartData, 'group')
    const mappedColors = getMappedColors(metaData.colors)

    // Create series for each group
    const series = Object.keys(groupedData)
      .sort((a, b) => parseInt(a) - parseInt(b))
      .map((groupIndex) => {
        const groupNum = parseInt(groupIndex)
        const dataPoints = groupedData[groupIndex]

        if (!dataPoints) {
          return {
            name: (groupTitles && groupTitles[groupNum]) || '',
            data: [],
            color: mappedColors[groupNum],
          }
        }

        // Sort by x index and extract values
        const sortedData = dataPoints.sort((a, b) => {
          const aX = a.x !== undefined ? a.x : 0
          const bX = b.x !== undefined ? b.x : 0
          return aX - bX
        })

        // Create array with values at correct x positions
        const data = new Array(categories.length).fill(0)
        sortedData.forEach((point) => {
          if (point.x !== undefined && point.x < categories.length) {
            data[point.x] = point.value
          }
        })

        return {
          name: (groupTitles && groupTitles[groupNum]) || '',
          data,
          color: mappedColors[groupNum],
        }
      })

    const colors = mappedColors

    const defOptions = {
      chart: {
        type: 'line',
        width: '100%',
        height: '100%',
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: isExpanded ? 3 : 2,
      },
      markers: {
        size: isExpanded ? 5 : 3,
        strokeWidth: 0,
        hover: {
          size: isExpanded ? 7 : 5,
        },
      },
      grid: {
        padding: {
          top: 20,
          right: 20,
          bottom: 10,
          left: 10,
        },
      },
      xaxis: {
        labels: {
          show: true,
        },
        categories: categories,
      },
      yaxis: {
        labels: {
          formatter: formatY,
        },
        min: 0,
      },
      colors: colors,
      legend: {
        show: showLegend,
        position: (metaData.legendPosition || 'bottom') as
          | 'top'
          | 'right'
          | 'bottom'
          | 'left',
        horizontalAlign: 'center',
      },
      title: {
        text: '',
        align: 'left',
      },
      tooltip: {
        y: {
          formatter: formatY,
        },
      },
      noData: {
        text: metaData.noDataMessage || 'No data found',
      },
    }

    if (isDevEnv()) {
      console.log(
        `>>> Processed V2 line chart data for chart "${metaData.title}":`,
        series,
      )
    }

    return { options: defOptions, data: series }
  } catch (error) {
    console.error(
      'Error processing line chart data:',
      error instanceof Error ? error.message : 'Unknown error',
    )

    // Return safe fallback configuration
    const fallbackOptions = {
      chart: {
        type: 'line',
        width: '100%',
        height: '100%',
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: isExpanded ? 3 : 2,
      },
      markers: {
        size: isExpanded ? 5 : 3,
        strokeWidth: 0,
        hover: {
          size: isExpanded ? 7 : 5,
        },
      },
      grid: {
        padding: {
          top: 20,
          right: 20,
          bottom: 10,
          left: 10,
        },
      },
      xaxis: {
        labels: {
          show: true,
        },
        categories: [],
      },
      yaxis: {
        labels: {
          formatter: (value: number) => value.toLocaleString(locale),
        },
        min: 0,
      },
      colors: [],
      legend: {
        show: false,
        position: 'bottom' as 'top' | 'right' | 'bottom' | 'left',
        horizontalAlign: 'center',
      },
      title: {
        text: '',
        align: 'left',
      },
      tooltip: {
        y: {
          formatter: (value: number) => value.toLocaleString(locale),
        },
      },
      noData: {
        text: 'Error loading chart data',
      },
    }

    return { options: fallbackOptions, data: [] }
  }
}
